import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { collection, doc, getDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { PortvilleResidency, PortvilleRoom, PortvilleRoomWithResidency } from '../../../types/portvilleRoom';
import { setAdminScrollTop } from '../../../utils/adminScroll';
import { useBackToPortvilleRoomsListOnPop } from '../../../hooks/useBackToPortvilleRoomsListOnPop';
import { markPortvilleRoomsRestoreOnNext } from '../../../utils/portvilleRoomsScroll';
import { calculateMoveOutCharges, getMoveOutInspectionDate } from '../../../utils/portvilleMoveOutCharges';
import MoveOutChargesSummary from '../../../components/admin/portville-rooms/MoveOutChargesSummary';

const MoveOutInspectionPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();

  const sigCanvasRef = useRef<SignatureCanvas>(null);

  const [room, setRoom] = useState<PortvilleRoomWithResidency | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [inspectorName, setInspectorName] = useState('');
  const [inspectionNotes, setInspectionNotes] = useState('');
  const [signatureSize, setSignatureSize] = useState({ width: 400, height: 200 });
  const [communicationUsed, setCommunicationUsed] = useState<boolean | null>(null);
  const [includePreviousMonth, setIncludePreviousMonth] = useState(false);
  const [inspectionDate, setInspectionDate] = useState(getMoveOutInspectionDate);
  const charges = useMemo(() => communicationUsed === null ? null
    : calculateMoveOutCharges(inspectionDate, communicationUsed, includePreviousMonth), [inspectionDate, communicationUsed, includePreviousMonth]);

  useEffect(() => {
    const refreshDate = () => setInspectionDate(getMoveOutInspectionDate());
    const interval = window.setInterval(refreshDate, 60000);
    window.addEventListener('focus', refreshDate);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshDate);
    };
  }, []);

  useBackToPortvilleRoomsListOnPop();

  const goToRoomsList = () => {
    markPortvilleRoomsRestoreOnNext();
    navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
  };

  useEffect(() => {
    setAdminScrollTop(0);
  }, []);

  type MoveOutInspectionItem = {
    id: string;
    category: '주방 및 거실' | '현관 및 욕실' | '반환품목';
    name: string;
    checked: boolean;
    notes: string;
  };

  const [inspectionItems, setInspectionItems] = useState<MoveOutInspectionItem[]>([
    // 주방 및 거실
    { id: 'kitchen_living_aircon', category: '주방 및 거실', name: '에어컨', checked: false, notes: '' },
    { id: 'kitchen_living_fridge', category: '주방 및 거실', name: '냉장고', checked: false, notes: '' },
    { id: 'kitchen_living_washer', category: '주방 및 거실', name: '세탁기', checked: false, notes: '' },
    { id: 'kitchen_living_induction', category: '주방 및 거실', name: '인덕션', checked: false, notes: '' },
    { id: 'kitchen_living_hood', category: '주방 및 거실', name: '후드', checked: false, notes: '' },
    { id: 'kitchen_living_upper_cabinet', category: '주방 및 거실', name: '주방 상부 수납장', checked: false, notes: '' },
    { id: 'kitchen_living_builtin', category: '주방 및 거실', name: '빌트인 가구', checked: false, notes: '' },
    { id: 'kitchen_living_desk', category: '주방 및 거실', name: '책상 가구', checked: false, notes: '' },
    { id: 'kitchen_living_desk_upper', category: '주방 및 거실', name: '책상 상부 수납장', checked: false, notes: '' },
    { id: 'kitchen_living_wardrobe', category: '주방 및 거실', name: '옷장', checked: false, notes: '' },

    // 현관 및 욕실
    { id: 'entry_bath_doorlock', category: '현관 및 욕실', name: '디지털 도어락', checked: false, notes: '' },
    { id: 'entry_bath_shoerack', category: '현관 및 욕실', name: '신발장', checked: false, notes: '' },
    { id: 'entry_bath_sink', category: '현관 및 욕실', name: '세면대', checked: false, notes: '' },
    { id: 'entry_bath_toilet', category: '현관 및 욕실', name: '양변기', checked: false, notes: '' },
    { id: 'entry_bath_upper_cabinet', category: '현관 및 욕실', name: '상부 수납장', checked: false, notes: '' },
    { id: 'entry_bath_shower_booth', category: '현관 및 욕실', name: '샤워부스', checked: false, notes: '' },
    { id: 'entry_bath_shower_head', category: '현관 및 욕실', name: '샤워기', checked: false, notes: '' }
    ,
    // 반환품목
    { id: 'return_remote_aircon', category: '반환품목', name: '리모컨류 (에어컨 1ea)', checked: false, notes: '' },
    { id: 'return_internet_tv', category: '반환품목', name: '인터넷+TV (인터넷, WIFI 및 TV 셋업박스)', checked: false, notes: '' },
    { id: 'return_fire_extinguisher', category: '반환품목', name: '소화기 (분말소화기)', checked: false, notes: '' }
  ]);

  const [meterElectricity, setMeterElectricity] = useState('');
  const [meterWater, setMeterWater] = useState('');
  const [meterGas, setMeterGas] = useState('');

  const inspectionCategories = useMemo(() => ([
    '주방 및 거실',
    '현관 및 욕실',
    '반환품목'
  ] as const), []);

  const applicableInspectionItems = useMemo(() => inspectionItems.filter(item =>
    communicationUsed !== false || item.id !== 'return_internet_tv'
  ), [inspectionItems, communicationUsed]);

  const groupedInspectionItems = useMemo(() => {
    const groups = inspectionCategories.reduce((acc, category) => {
      acc[category] = [];
      return acc;
    }, {} as Record<(typeof inspectionCategories)[number], MoveOutInspectionItem[]>);

    applicableInspectionItems.forEach((item) => {
      groups[item.category].push(item);
    });
    return groups;
  }, [applicableInspectionItems, inspectionCategories]);

  const updateSignatureSize = useCallback(() => {
    const isMobile = window.innerWidth < 768;
    setSignatureSize(isMobile ? { width: 320, height: 160 } : { width: 400, height: 200 });
  }, []);

  useEffect(() => {
    updateSignatureSize();
    window.addEventListener('resize', updateSignatureSize);
    return () => window.removeEventListener('resize', updateSignatureSize);
  }, [updateSignatureSize]);

  useEffect(() => {
    if (roomId && hasAccess) {
      void loadRoom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, hasAccess]);

  const loadRoom = async () => {
    if (!roomId) return;
    try {
      setLoading(true);
      setError(null);

      const roomDoc = await getDoc(doc(db, 'portville_rooms', roomId));
      if (!roomDoc.exists()) {
        setError('호실을 찾을 수 없습니다.');
        return;
      }

      const roomData = roomDoc.data() as PortvilleRoom;
      if (!roomData.currentResidencyId) {
        setError('현재 입주 기록이 없는 호실입니다.');
        return;
      }

      const residencyDoc = await getDoc(doc(db, 'portville_residencies', roomData.currentResidencyId));
      if (!residencyDoc.exists()) {
        setError('입주 기록을 찾을 수 없습니다.');
        return;
      }

      const residencyData: any = residencyDoc.data();
      const currentResidency: PortvilleResidency = {
        id: residencyDoc.id,
        roomId: residencyData.roomId,
        roomNumber: residencyData.roomNumber,
        residentUid: residencyData.residentUid,
        residentInfo: residencyData.residentInfo,
        occupancyPeriod: residencyData.occupancyPeriod
          ? {
              startDate: residencyData.occupancyPeriod.startDate.toDate(),
              endDate: residencyData.occupancyPeriod.endDate.toDate()
            }
          : undefined,
        roommateInfo: residencyData.roommateInfo,
        roommateContact: residencyData.roommateContact,
        vehicleInfo: residencyData.vehicleInfo,
        notes: residencyData.notes,
        status: residencyData.status,
        createdAt: residencyData.createdAt.toDate(),
        updatedAt: residencyData.updatedAt.toDate(),
        completedAt: residencyData.completedAt?.toDate()
      } as any;

      const combinedRoom: PortvilleRoomWithResidency = {
        id: roomDoc.id,
        roomNumber: roomData.roomNumber,
        roomType: roomData.roomType || '호실',
        status: roomData.status,
        createdAt: (roomData.createdAt as any).toDate ? (roomData.createdAt as any).toDate() : (roomData.createdAt as any),
        updatedAt: (roomData.updatedAt as any).toDate ? (roomData.updatedAt as any).toDate() : (roomData.updatedAt as any),
        currentResidencyId: roomData.currentResidencyId,
        facilityInfo: roomData.facilityInfo
      };

      combinedRoom.currentResident = currentResidency.residentInfo;
      combinedRoom.residentUid = currentResidency.residentUid;
      combinedRoom.occupancyPeriod = currentResidency.occupancyPeriod;
      combinedRoom.roommateInfo = currentResidency.roommateInfo;
      combinedRoom.roommateContact = (currentResidency as any).roommateContact;
      combinedRoom.vehicleInfo = currentResidency.vehicleInfo;
      combinedRoom.notes = currentResidency.notes;

      setRoom(combinedRoom);

      if (combinedRoom.status !== 'occupied') {
        setError('입주중(occupied) 상태의 호실만 퇴거점검이 가능합니다.');
        return;
      }
    } catch (e) {
      console.error('퇴거점검 - 호실 정보 로드 실패:', e);
      setError('호실 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const updateInspectionItem = (id: string, field: 'checked' | 'notes', value: boolean | string) => {
    setInspectionItems(prev => prev.map(item => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const clearSignature = () => {
    sigCanvasRef.current?.clear();
  };

  const completeMoveOutInspection = async () => {
    if (saving || !roomId || !room?.currentResidencyId) return;
    if (!charges) {
      alert('통신 사용 유무를 선택해주세요.');
      return;
    }
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      alert('서명을 입력해주세요.');
      return;
    }
    if (!inspectorName.trim()) {
      alert('점검자 이름을 입력해주세요.');
      return;
    }

    const confirmed = window.confirm('퇴거점검을 완료하시겠습니까? (계약종료는 별도 처리)');
    if (!confirmed) return;

    const now = Timestamp.now();
    const saveDate = getMoveOutInspectionDate(now.toDate());
    if (saveDate !== inspectionDate) {
      setInspectionDate(saveDate);
      alert('점검일이 변경되어 금액을 갱신했습니다. 안내 금액을 확인한 후 다시 저장해주세요.');
      return;
    }

    try {
      setSaving(true);

      const canvas = sigCanvasRef.current.getCanvas();
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((b) => resolve(b!), 'image/png');
      });

      const signatureRef = ref(storage, `move_out_inspection_signatures/${roomId}_${Date.now()}.png`);
      await uploadBytes(signatureRef, blob);
      const signatureUrl = await getDownloadURL(signatureRef);

      const batch = writeBatch(db);
      const inspectionRef = doc(collection(db, 'portville_inspections'));
      batch.set(inspectionRef, {
        inspectionType: 'move-out',
        roomId,
        roomNumber: room.roomNumber,
        residencyId: room.currentResidencyId,
        residentUid: room.residentUid || null,
        residentInfo: room.currentResident || null,
        occupancyPeriod: room.occupancyPeriod
          ? {
              startDate: Timestamp.fromDate(room.occupancyPeriod.startDate),
              endDate: Timestamp.fromDate(room.occupancyPeriod.endDate)
            }
          : null,
        inspectionItems: applicableInspectionItems.map(item => ({
          id: item.id,
          category: item.category,
          name: item.name,
          checked: item.checked,
          notes: item.notes
        })),
        meterReadings: {
          electricity: meterElectricity.trim(),
          water: meterWater.trim(),
          gas: meterGas.trim()
        },
        inspectorName: inspectorName.trim(),
        inspectionNotes,
        charges,
        signatureUrl,
        inspectedAt: now,
        createdAt: now
      });

      batch.update(doc(db, 'portville_residencies', room.currentResidencyId), {
        moveOutInspectionId: inspectionRef.id,
        moveOutInspectionSignedAt: now,
        moveOutInspectionInspectorName: inspectorName.trim(),
        moveOutInspectionSignatureUrl: signatureUrl,
        moveOutInspectionItems: applicableInspectionItems,
        moveOutInspectionNotes: inspectionNotes,
        moveOutInspectionCharges: charges,
        moveOutMeterReadings: {
          electricity: meterElectricity.trim(),
          water: meterWater.trim(),
          gas: meterGas.trim()
        },
        updatedAt: now
      } as any);
      await batch.commit();
      alert('퇴거점검이 완료되었습니다. 계약종료는 호실 목록에서 별도로 진행해주세요.');
      goToRoomsList();
    } catch (e) {
      console.error('퇴거점검 완료 처리 실패:', e);
      alert('퇴거점검 저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  if (!hasAccess) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">호실 정보를 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-800">{error}</div>
        <div className="mt-6">
          <button
            onClick={goToRoomsList}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            목록으로
          </button>
        </div>
      </div>
    );
  }

  if (!room) return null;

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      <div className="max-w-5xl mx-auto pt-8 px-4 pb-16">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-800">퇴거점검 - {room.roomNumber}호</h1>
          <button
            onClick={goToRoomsList}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            목록으로
          </button>
        </div>

        <fieldset disabled={saving} className="mt-6 min-w-0 bg-white rounded-lg shadow p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-500">입주자</div>
              <div className="mt-1 font-medium text-gray-900">{room.currentResident?.name || '-'}</div>
              <div className="text-sm text-gray-600">{room.currentResident?.phoneNumber || '-'}</div>
              <div className="text-sm text-gray-600">{room.currentResident?.organization || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">입주 기간</div>
              <div className="mt-1 text-gray-900">
                {room.occupancyPeriod
                  ? `${room.occupancyPeriod.startDate.toLocaleDateString('ko-KR')} ~ ${room.occupancyPeriod.endDate.toLocaleDateString('ko-KR')}`
                  : '-'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">점검자 이름 *</label>
              <input
                type="text"
                value={inspectorName}
                onChange={(e) => setInspectorName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">점검 메모</label>
            <textarea
              rows={3}
              value={inspectionNotes}
              onChange={(e) => setInspectionNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="퇴거 점검 관련 메모를 입력하세요"
            />
          </div>

          <div className="border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">퇴거 점검 항목</h2>
            <div className="space-y-6">
              {inspectionCategories.map((category) => (
                <div key={category}>
                  <div className="text-base font-semibold text-slate-800 mb-3">
                    ({category})
                  </div>
                  <div className="space-y-3">
                    {groupedInspectionItems[category].map((item) => (
                      <div key={item.id} className="flex flex-col gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={item.checked}
                            onChange={(e) => updateInspectionItem(item.id, 'checked', e.target.checked)}
                          />
                          <span className="font-medium text-gray-800">{item.name}</span>
                        </label>
                        <input
                          type="text"
                          value={item.notes}
                          onChange={(e) => updateInspectionItem(item.id, 'notes', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                          placeholder="비고"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">퇴거 납부금액 안내</h2>
            <fieldset className="mb-4">
              <legend className="text-sm font-medium text-gray-700 mb-2">전월 관리비 포함 여부</legend>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input type="radio" name="includePreviousMonth" checked={!includePreviousMonth}
                    onChange={() => setIncludePreviousMonth(false)} />
                  미포함
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="includePreviousMonth" checked={includePreviousMonth}
                    onChange={() => setIncludePreviousMonth(true)} />
                  포함
                </label>
              </div>
            </fieldset>
            <fieldset className="mb-4">
              <legend className="text-sm font-medium text-gray-700 mb-2">통신 사용 유무 *</legend>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input type="radio" name="communicationUsed" checked={communicationUsed === true}
                    onChange={() => setCommunicationUsed(true)} />
                  사용 유 ({includePreviousMonth ? '22,000' : '11,000'}원, 부가세 포함)
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="communicationUsed" checked={communicationUsed === false}
                    onChange={() => setCommunicationUsed(false)} />
                  사용 무 (0원)
                </label>
              </div>
            </fieldset>
            {charges ? <MoveOutChargesSummary charges={charges} recipient={{
              name: room.currentResident?.name, phoneNumber: room.currentResident?.phoneNumber
            }} disabled={saving} />
              : <p className="text-sm text-gray-600">통신 사용 유무를 선택하면 항목별 금액과 총 납부금액을 안내합니다.</p>}
          </div>

          <div className="border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">서명</h2>
            <div className="bg-white border border-gray-200 rounded-md p-3 inline-block">
              <SignatureCanvas
                ref={sigCanvasRef}
                penColor="black"
                canvasProps={{
                  width: signatureSize.width,
                  height: signatureSize.height,
                  className: 'signature-canvas'
                }}
              />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={clearSignature}
                className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                서명 지우기
              </button>
            </div>
          </div>

          <div className="border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">검침수치 (선택)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">전기</label>
                <input
                  type="text"
                  value={meterElectricity}
                  onChange={(e) => setMeterElectricity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="전기 검침값"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">수도</label>
                <input
                  type="text"
                  value={meterWater}
                  onChange={(e) => setMeterWater(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="수도 검침값"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">가스</label>
                <input
                  type="text"
                  value={meterGas}
                  onChange={(e) => setMeterGas(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="가스 검침값"
                />
              </div>
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="button"
              disabled={saving}
              onClick={completeMoveOutInspection}
              className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
            >
              {saving ? '처리 중...' : '퇴거점검 완료'}
            </button>
          </div>
        </fieldset>
      </div>
    </div>
  );
};

export default MoveOutInspectionPage;
